/**
 * Opposites of Numbers generator (6.NR.2.2).
 *
 * The opposite of a number is its reflection across zero on the number line.
 * Covers the opposite of an integer, the opposite of an opposite (double
 * negative), and the opposite of a fraction. The value is chosen first and the
 * answer is computed with Rational.neg(), so display and answer always agree.
 *
 * Families:
 *   F1: opposite of an integer
 *   F2: opposite of an opposite (double negative), e.g. -(-4)
 *   F3: opposite of a fraction
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "opposites" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
  readonly denoms: readonly number[];
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 12, denoms: [2, 4, 5, 10] },
  standard: { families: ["F1", "F2", "F3"], max: 25, denoms: [2, 4, 5, 8, 10] },
  hard: { families: ["F1", "F2", "F3"], max: 60, denoms: [4, 8, 20, 25] },
  challenge: { families: ["F2", "F3"], max: 120, denoms: [8, 20, 25] },
};

/** Wrap a negative value in parentheses for readable LaTeX, e.g. "(-5)". */
function paren(v: Rational): string {
  return v.isNegative() ? `(${v.toLatex()})` : v.toLatex();
}

export class OppositesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let value: Rational;
      let promptText: string;
      let answer: Rational;
      let stepDesc: string;
      let stepLatex: string;

      switch (family) {
        case "F1": {
          const mag = rng.int(1, cfg.max);
          value = rat(mag * rng.sign(0.5));
          answer = value.neg();
          promptText = `What is the opposite of ${value.toString()}?`;
          stepDesc = "The opposite of a number is the same distance from zero on the other side.";
          stepLatex = `-(${value.toLatex()}) = ${answer.toLatex()}`;
          break;
        }
        case "F2": {
          // Opposite of an opposite: -(-a) = a. Force the inner value negative
          // so the prompt is a genuine double negative, e.g. -(-4).
          const mag = rng.int(1, cfg.max);
          value = rat(-mag); // the "-a" being opposed again
          answer = value.neg(); // = a
          promptText = `Simplify: -(${value.toString()})`;
          stepDesc = "The opposite of the opposite of a number is the number itself.";
          stepLatex = `-(${paren(value)}) = ${answer.toLatex()}`;
          break;
        }
        case "F3": {
          const den = rng.pick(cfg.denoms);
          const num = rng.int(1, den + Math.floor(den / 2));
          const mag = rat(num, den);
          value = mag.mul(rat(rng.sign(0.5)));
          answer = value.neg();
          promptText = `What is the opposite of ${value.toString()}?`;
          stepDesc = "Flip the sign; the size (distance from zero) stays the same.";
          stepLatex = `-(${paren(value)}) = ${answer.toLatex()}`;
          break;
        }
      }

      // Skip the trivial opposite of zero.
      if (value.isZero()) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: `-(${paren(value)})` },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex: stepLatex }],
        hints: [
          { level: 1, text: "The opposite reflects the number across zero." },
          { level: 2, text: "Opposites have the same distance from zero but the other sign; -(-a) = a." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const opposites = new OppositesGenerator();
